import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { merge, Observable } from 'rxjs'
import { Catalogue } from '../models/catalogue'
import { UrlCollection } from '../url-collection'
import { HttpOptionsFactory } from '../models/http/http-options-factory'
import { TreeNode } from 'primeng/api'
import { ActionsSubject, Store } from '@ngrx/store'
import { RootStoreState } from '../root-store/root-index'
import { CatalogueActions } from '../root-store/catalogue-store/catalogue-index'
import { filter, map, take } from 'rxjs/operators'
import { ofType } from '@ngrx/effects'
import { EntityReference } from '../modules/dto'

@Injectable({
  providedIn: 'root'
})
export class CatalogueService {
  constructor(
    private httpClient: HttpClient,
    private store$: Store<RootStoreState.State>,
    private actions$: ActionsSubject
  ) {}

  getAllCatalogues(): Promise<TreeNode[]> {
    return this.httpClient
      .get<any>(UrlCollection.Catalogues.ALL(), new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build())
      .toPromise()
      .then((res) => <TreeNode[]>res.data)
  }

  createCatalogue(name: string, description: string): Observable<Catalogue> {
    return this.httpClient.post<Catalogue>(
      UrlCollection.Catalogues.CREATE(),
      JSON.stringify({ name: name, description: description }),
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  getCatalogue(hrid: string): Observable<Catalogue> {
    if (!hrid) return null

    const catalogue$ = merge(
      this.actions$.pipe(ofType(CatalogueActions.catalogueRetrieved)),
      this.actions$.pipe(ofType(CatalogueActions.catalogueRequestFailed))
    ).pipe(
      filter((action) => action.hrid === hrid),
      take(1),
      map((action) => action['catalogue'])
    )
    // TODO this is wrong because it leads to scenarios where the subscribe (when done in html) comes too late as the "next" from store already went through
    this.store$.dispatch(CatalogueActions.catalogueRequested({ hrid: hrid }))
    return catalogue$
  }

  // Gets catalogue from backend without using the cache in the store
  getCatalogueUncached(name: string): Observable<Catalogue> {
    return this.httpClient.post<Catalogue>(
      UrlCollection.Catalogues.GET(),
      JSON.stringify({ name: name }),
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  deleteCatalogue(catalogueId: string) {
    this.store$.dispatch(CatalogueActions.catalogueModified({ id: Number.parseInt(catalogueId) }))
    return this.httpClient.get(
      UrlCollection.Catalogues.DELETE() + '/' + catalogueId,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  addEntry(catalogueId: string, name: string, description: string): Observable<any> {
    this.store$.dispatch(CatalogueActions.catalogueModified({ id: Number.parseInt(catalogueId) }))
    return this.httpClient.post<any>(
      UrlCollection.Catalogues.ADDENTRY() + '/' + catalogueId,
      JSON.stringify({ name: name, description: description }),
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  removeEntry(catalogueId: string, entryId: string) {
    this.store$.dispatch(CatalogueActions.catalogueModified({ id: Number.parseInt(catalogueId) }))
    return this.httpClient.get(
      UrlCollection.Catalogues.REMOVEENTRY() + '/' + entryId,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  getCatalogueByExpression(expression: string, entityReference: EntityReference): Observable<Catalogue> {
    const payload = {
      expression: expression,
      entityReference: entityReference
    }

    return this.httpClient.post<Catalogue>(
      UrlCollection.Catalogues.GETBYEXPRESSION(),
      payload,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }
}
